import clsx from 'clsx'
import Expander from 'components/utils/Expander'
import { rem } from 'polished'
import type { PropsWithChildren, ReactNode } from 'react'
import styled from 'styled-components'
import { tierGradientSecondaryBackgroundStyle } from 'styles/utils/tierStyles'

// Matches the height transition inside `components/utils/Expander`, which does not export its own.
const FRAME_FADE_DURATION = 300

const Panel = styled.div`
  position: relative;
  border-radius: ${rem(12)};
  overflow: hidden;
`

/**
 * The gold behind the strip and its rows. A real element rather than a pseudo-element because the
 * tier gradient is selected by a `tier-*` class on the element it paints.
 */
const Frame = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity ${FRAME_FADE_DURATION}ms;
  ${tierGradientSecondaryBackgroundStyle}

  &.expanded {
    opacity: 1;
  }
`

// The frame is absolutely positioned, so it would paint over static siblings. Positioning the
// content puts the strip and rows back on top of it.
const Content = styled.div`
  position: relative;
`

// The gaps around the rows live inside the region whose height animates, so they collapse with it
// rather than disappearing on the first frame.
const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  padding-block: ${rem(8)} ${rem(12)};
`

interface Props extends PropsWithChildren {
  tier: App.LoyaltyTier
  isExpanded: boolean
  /** The upgrade rows revealed beneath the strip. Always rendered so their height can be animated. */
  rows: ReactNode
}

function LoyaltyStayUpgradeExpandedPanel({ tier, isExpanded, rows, children }: Props) {
  return <Panel>
    <Frame className={clsx(`tier-${tier}`, { expanded: isExpanded })} />
    <Content>
      {children}
      <Expander expanded={isExpanded}>
        <Rows inert={!isExpanded}>{rows}</Rows>
      </Expander>
    </Content>
  </Panel>
}

export default LoyaltyStayUpgradeExpandedPanel
